import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  CancelIcon,
  EditIcon,
  HFBox,
  HFButton,
  HFCard,
  HFChip,
  HFDataTable,
  formatUtcDateTime,
  formatUtcDayTime,
  hfAlpha,
  HFKpiTile,
  HFModal,
  HFSkeleton,
  HFTooltip,
  HistoryIcon,
  InfoIcon,
  PauseIcon,
  PlayArrowIcon,
  actionsColumn,
  filterRowsByFields,
  type GridRowId,
  type HFChipTone,
  type HFDataTableRowTree,
} from '@hf/designSystem/v1';
import {
  OMS_BLOTTER_COLUMN_IDS,
  OMS_LAYOUT_COLUMN_MAX_WIDTH,
  OMS_LAYOUT_COLUMN_MIN_WIDTH,
  OMS_ORDER_STATUS_LABELS,
  OMS_RECEIVED_HINT,
  omsOrderHistorySchema,
  omsOrderStatusLabel,
  type OmsBlotterColumnId,
  type OmsFill,
  type OmsLayoutColumn,
  type OmsLayoutSort,
  type OmsOrder,
  type OmsOrderBlotterFilters,
  type OmsOrderHistory,
} from '@hf/sharedContract';
import { GA, track } from '@core/utils/analytics';
import { csvFileName, downloadCsv, toCsv } from '@core/utils/csv';
import { useHttp } from '@core/utils/http';
import { monitorPaneSx } from '../layout/monitorPane';
import { loadMonitorLayout, queueMonitorLayoutSave, syncMonitorLayout } from '../layout/monitorLayoutWrite';
import { ColumnVisibilityList } from './ColumnVisibilityList';
import { OrderStatusStrip, type OrderStatusFilter } from './OrderStatusStrip';
import { PanelHeader } from './PanelHeader';
import { TableSkeleton } from './TableSkeleton';

const TODAY_ONLY = 'Today only: the opening position is not connected.';

/** Labels and widths for the shared Order Monitor column ids. `hint` columns are instrument figures only. */
const BLOTTER_COLUMN_DETAILS: Record<OmsBlotterColumnId, { label: string; width: number; hint?: string }> = {
  exchange: { label: 'Exch', width: 90 },
  product: { label: 'Product', width: 110 },
  year: { label: 'Year', width: 80 },
  month: { label: 'Month', width: 90 },
  symbol: { label: 'Symbol', width: 120 },
  status: { label: 'Status', width: 170 },
  position: { label: 'Position', width: 110, hint: `Net lots filled on the trade day. ${TODAY_ONLY}` },
  workingBuys: { label: 'Working buys', width: 120, hint: 'Unfilled lots of Released, Received and Working buy orders.' },
  workingSells: { label: 'Working sells', width: 120, hint: 'Unfilled lots of Released, Received and Working sell orders.' },
  projected: { label: 'Projected', width: 120, hint: `Position + working buys − working sells. ${TODAY_ONLY}` },
  side: { label: 'Side', width: 90 },
  target: { label: 'Target', width: 90 },
  filled: { label: 'Filled', width: 170 },
  working: { label: 'Working', width: 100 },
  inactive: { label: 'Inactive', width: 100 },
  orderId: { label: 'Order ID', width: 120 },
  first: { label: 'First', width: 80 },
  avg: { label: 'Avg', width: 80 },
  last: { label: 'Last', width: 80 },
};
const BLOTTER_COLUMNS = OMS_BLOTTER_COLUMN_IDS.map((id) => ({ id, ...BLOTTER_COLUMN_DETAILS[id] }));

const TAB = 'monitor';
const COMPACT_BUTTON_SX = { minHeight: 28, px: 1.25, py: 0.25, fontSize: 12 } as const;
const CHROME_SX = { display: 'flex', flexDirection: 'column', gap: 2, mb: 2, flexShrink: 0 } as const;
const CHROME_ROW_SX = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' } as const;
const CLUSTER_SX = { display: 'flex', alignItems: 'center', gap: 1 } as const;
const DASH = '—';
const BLOTTER_ROW_PX = 36;
const EXECUTION_LINE_PX = 28;
const EXECUTION_PAD_PX = 12;
const AVG_DECIMALS = 4;

type FillBurst = {
  key: string;
  clock: string;
  fills: OmsFill[];
  qty: number;
  priceMin: number;
  priceMax: number;
};

type AccountLine = {
  account: string;
  instructionQty: number;
  allocPct: number;
  allocatedQty: number;
  averageFillPrice: number | null;
  isHost: boolean;
  fillCount: number;
  burstSummary: string | null;
};

type ExecutionRow = {
  id: string;
  kind: 'execution';
  orderId: string;
  accounts: AccountLine[];
  fillCount: number;
  hostAccount: string;
};

type OrderRow = {
  id: string;
  kind: 'order';
  children?: ExecutionRow[];
  exchange: string;
  product: string;
  year: string;
  month: string;
  symbol: string;
  status: string;
  statusTone: HFChipTone;
  side: string;
  target: number;
  orderId: string;
  filled: number;
  working: number;
  inactive: number;
  first: string;
  avg: string;
  last: string;
  avgPrice: number | null;
};

/** One contract or spread. Its orders sit underneath. */
type InstrumentRow = {
  id: string;
  kind: 'instrument';
  children: OrderRow[];
  exchange: string;
  product: string;
  year: string;
  month: string;
  symbol: string;
  status: string;
  /** Null when the positions read is missing. */
  position: number | null;
  workingBuys: number;
  workingSells: number;
  projected: number | null;
};

type BlotterRow = InstrumentRow | OrderRow | ExecutionRow;

type FillTapeRow = {
  id: string;
  time: string;
  qty: string;
  positionAfter: string;
  price: string;
  execId: string;
};

function lots(value: number): string {
  if (!Number.isFinite(value)) return DASH;
  return String(Math.abs(value));
}

function signedLots(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  return value > 0 ? `+${value}` : String(value);
}

function percent(value: number): string {
  return `${value.toFixed(2)}%`;
}

function priceText(value: number | null | undefined, showZero: boolean): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  if (value === 0 && !showZero) return DASH;
  return String(value);
}

function utcClock(iso: string): string | undefined {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  return [date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()].map((part) => String(part).padStart(2, '0')).join(':');
}

function utcDay(iso: string): string | undefined {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
}

function formatPrice(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return DASH;
  return value.toFixed(digits);
}

/** One timestamp when every fill shares it. Same day keeps the date once. */
function fillRangeLabel(fills: readonly { tradeTime: string }[]): string | undefined {
  if (fills.length === 0) return undefined;
  const first = fills[0]!.tradeTime;
  const last = fills[fills.length - 1]!.tradeTime;
  const firstDay = utcDay(first);
  const lastDay = utcDay(last);
  const firstClock = utcClock(first);
  const lastClock = utcClock(last);
  if (!firstClock || !lastClock) return formatUtcDateTime(first);
  if (firstDay === lastDay && firstClock === lastClock) return formatUtcDateTime(first);
  if (firstDay === lastDay) return `${formatUtcDateTime(first).replace(/, \d{2}:\d{2}:\d{2}$/, '')}, ${firstClock} – ${lastClock}`;
  return `${formatUtcDateTime(first)} – ${formatUtcDateTime(last)}`;
}

function secondKey(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toISOString().slice(0, 19);
}

function groupFillsBySecond(fills: readonly OmsFill[]): FillBurst[] {
  const buckets = new Map<string, OmsFill[]>();
  for (const fill of fills) {
    const key = secondKey(fill.tradeTime);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(fill);
    else buckets.set(key, [fill]);
  }
  return [...buckets.entries()].map(([key, group]) => {
    const prices = group.map((fill) => fill.price);
    return {
      key,
      clock: formatUtcDayTime(group[0]!.tradeTime),
      fills: group,
      qty: group.reduce((total, fill) => total + Math.abs(fill.qty), 0),
      priceMin: Math.min(...prices),
      priceMax: Math.max(...prices),
    };
  });
}

function burstSummaryText(bursts: readonly FillBurst[], fillCount: number): string | null {
  if (fillCount === 0) return null;
  const recent = bursts.slice(-3);
  const earlier = bursts.length - recent.length;
  const parts = recent.map((burst) => {
    const range =
      burst.priceMin === burst.priceMax ? priceText(burst.priceMin, true) : `${priceText(burst.priceMin, true)}–${priceText(burst.priceMax, true)}`;
    const fillLabel = burst.fills.length === 1 ? '1 fill' : `${burst.fills.length} fills`;
    return `${burst.clock} · ${fillLabel} · ${range}`;
  });
  if (earlier > 0) parts.unshift(`+${earlier} earlier`);
  return parts.join(' · ');
}

/**
 * Fills are recorded on the order, not on an instruction. They are shown once,
 * under the allocation with the most quantity actually allocated.
 */
function executionDetails(order: OmsOrder): ExecutionRow | undefined {
  const recorded = order.allocations ?? [];
  const fills = order.fills ?? [];
  if (recorded.length === 0 && fills.length === 0) return undefined;
  const instructions = recorded.length > 0 ? recorded : [{ clearingAccount: null, instructionQty: 0, allocPct: 0, allocatedQty: 0 }];
  let host = 0;
  let most = -1;
  instructions.forEach((allocation, index) => {
    const qty = allocation.allocatedQty ?? 0;
    if (qty > most) {
      most = qty;
      host = index;
    }
  });
  const orderQty = Math.abs(order.qty);
  const orderFilled = Math.abs(order.filledQty);
  const bursts = groupFillsBySecond(fills);
  const summary = burstSummaryText(bursts, fills.length);
  const accounts: AccountLine[] = instructions.map((allocation, index) => {
    const account = allocation.clearingAccount?.trim() || 'Unallocated';
    const isHost = index === host;
    const allocatedQty = allocation.allocatedQty ?? 0;
    const instructionQty = allocation.instructionQty > 0 ? allocation.instructionQty : instructions.length === 1 ? orderQty : 0;
    const displayAllocated = recorded.length === 0 && isHost && fills.length > 0 ? orderFilled : allocatedQty;
    return {
      account,
      instructionQty,
      allocPct: allocation.allocPct,
      allocatedQty: displayAllocated,
      averageFillPrice: allocation.averageFillPrice ?? (isHost ? (order.averageFillPrice ?? null) : null),
      isHost,
      fillCount: isHost ? fills.length : 0,
      burstSummary: isHost ? summary : null,
    };
  });
  return {
    id: `${order.orderId}-execution`,
    kind: 'execution',
    orderId: String(order.orderId),
    accounts,
    fillCount: fills.length,
    hostAccount: accounts[host]?.account ?? 'Unallocated',
  };
}

function averagePrice(order: OmsOrder): number | null {
  if (Math.abs(order.filledQty) <= 0) return null;
  if (order.averageFillPrice != null && Number.isFinite(order.averageFillPrice)) return order.averageFillPrice;
  const fills = order.fills ?? [];
  if (fills.length === 0) return null;
  const qty = fills.reduce((total, fill) => total + Math.abs(fill.qty), 0);
  if (qty <= 0) return null;
  return fills.reduce((total, fill) => total + Math.abs(fill.qty) * fill.price, 0) / qty;
}

function dash(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  return trimmed || DASH;
}

function statusTone(label: string): HFChipTone {
  if (label === OMS_ORDER_STATUS_LABELS.Submitted || label === OMS_ORDER_STATUS_LABELS.PartiallyFilled) return 'working';
  if (label === OMS_ORDER_STATUS_LABELS.Pending) return 'pending';
  if (label === OMS_ORDER_STATUS_LABELS.Filled) return 'complete';
  if (label === OMS_ORDER_STATUS_LABELS.Unsubmitted) return 'neutral';
  return 'off';
}

/** Filled, then working, then inactive lots, each as a share of the target. */
function FillBar({ row }: { row: OrderRow }) {
  const share = (value: number) => `${row.target > 0 ? Math.min(100, (value / row.target) * 100) : 0}%`;
  const title = `${lots(row.filled)} filled · ${lots(row.working)} working · ${lots(row.inactive)} inactive, of ${lots(row.target)}`;
  return (
    <HFBox title={title} sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
      <HFBox sx={{ flex: 1, minWidth: 28, height: 6, borderRadius: 1, bgcolor: 'divider', overflow: 'hidden', display: 'flex' }}>
        <HFBox data-part="filled" style={{ width: share(row.filled) }} sx={{ bgcolor: 'primary.main' }} />
        <HFBox data-part="working" style={{ width: share(row.working) }} sx={{ bgcolor: (theme) => hfAlpha(theme.palette.primary.main, 0.35) }} />
        <HFBox data-part="inactive" style={{ width: share(row.inactive) }} sx={{ bgcolor: 'action.disabled' }} />
      </HFBox>
      <HFBox sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{`${lots(row.filled)}/${lots(row.target)}`}</HFBox>
    </HFBox>
  );
}

function HintHeader({ label, hint }: { label: string; hint: string }) {
  return (
    <HFBox component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <HFBox component="span" className="MuiDataGrid-columnHeaderTitle" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </HFBox>
      <HFTooltip title={hint}>
        <HFBox
          component="button"
          type="button"
          aria-label={hint}
          sx={{ display: 'inline-flex', p: 0, border: 0, bgcolor: 'transparent', color: 'text.secondary', cursor: 'help', lineHeight: 0 }}
        >
          <InfoIcon sx={{ fontSize: 14 }} />
        </HFBox>
      </HFTooltip>
    </HFBox>
  );
}

function MiniAllocBar({ allocated, instruction }: { allocated: number; instruction: number }) {
  const pct = instruction > 0 ? Math.min(100, (allocated / instruction) * 100) : 0;
  return (
    <HFBox sx={{ width: 48, height: 4, borderRadius: 1, bgcolor: 'divider', overflow: 'hidden', flexShrink: 0 }}>
      <HFBox sx={{ width: `${pct}%`, height: '100%', bgcolor: 'primary.main' }} />
    </HFBox>
  );
}

function executionPanelHeight(accounts: number): number {
  return EXECUTION_PAD_PX + Math.max(1, accounts) * EXECUTION_LINE_PX;
}

function renderExecutionPanel(row: ExecutionRow, onViewFills: (orderId: string) => void): ReactNode {
  const multi = row.accounts.length > 1;
  return (
    <HFBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        fontSize: 12.5,
        // Cap to the visible blotter width — the panel cell spans every column, so
        // stretch + ml:auto would park View fills past the horizontal scroll edge.
        width: 'min(100%, 520px)',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {row.accounts.map((account, index) => {
        const qtyLabel =
          account.instructionQty > 0 ? `${lots(account.allocatedQty)} of ${lots(account.instructionQty)}` : `${lots(account.allocatedQty)} allocated`;
        const detail = [
          qtyLabel,
          multi && account.allocPct > 0 ? percent(account.allocPct) : '',
          account.averageFillPrice != null && account.allocatedQty > 0 ? `avg ${priceText(account.averageFillPrice, true)}` : '',
          account.isHost ? account.burstSummary : '',
        ]
          .filter(Boolean)
          .join(' · ');
        return (
          <HFBox key={`${row.id}-${account.account}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
            {account.account === 'Unallocated' ? (
              <HFChip tone="neutral" label="Unallocated" />
            ) : (
              <HFBox component="span" sx={{ fontWeight: 700, flexShrink: 0 }}>
                {account.account}
              </HFBox>
            )}
            {multi ? <MiniAllocBar allocated={account.allocatedQty} instruction={account.instructionQty} /> : null}
            <HFBox
              component="span"
              sx={{
                flex: 1,
                minWidth: 0,
                color: 'text.secondary',
                fontVariantNumeric: 'tabular-nums',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={detail}
            >
              {detail || (account.isHost && account.fillCount === 0 ? 'No fills yet.' : '')}
            </HFBox>
            {account.isHost && account.fillCount > 0 ? (
              <HFButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onViewFills(row.orderId);
                }}
                sx={{ flexShrink: 0, whiteSpace: 'nowrap', borderRadius: 999, minHeight: 26, px: 1.25, py: 0, fontSize: 12 }}
              >
                {account.fillCount === 1 ? 'View 1 fill' : `View ${account.fillCount} fills`}
              </HFButton>
            ) : null}
          </HFBox>
        );
      })}
    </HFBox>
  );
}

const HISTORY_SECTION = /^\[[^\]\r\n]+\]$/;

function HistoryText({ text }: { text: string }) {
  const lines = text.split(/\r?\n/);
  return (
    <HFBox
      data-testid="order-history"
      sx={{
        p: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
      }}
    >
      {lines.map((line, index) => {
        const section = HISTORY_SECTION.test(line);
        return (
          <HFBox
            key={index}
            sx={{
              fontWeight: section ? 800 : 500,
              fontSize: section ? 12 : 14,
              letterSpacing: section ? '0.04em' : 0,
              lineHeight: 1.5,
              mt: section && index > 0 ? 1.5 : 0,
              minHeight: line ? undefined : 8,
            }}
          >
            {line || '\u00a0'}
          </HFBox>
        );
      })}
    </HFBox>
  );
}

const HISTORY_BODY_PX = 420;

function OrderHistoryModal({ orderId, onClose }: { orderId: string | null; onClose: () => void }) {
  const { request } = useHttp();
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    setText(null);
    setFailed(false);
    void request<OmsOrderHistory>(`/oms/orders/${orderId}/history`, { responseSchema: omsOrderHistorySchema }).then((result) => {
      if (cancelled) return;
      if (result.status === 200 && result.data) {
        setText(result.data.text);
        return;
      }
      setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [orderId, request]);

  return (
    <HFModal
      open={orderId != null}
      onClose={onClose}
      title={orderId ? `History for order ${orderId}` : 'History'}
      maxWidth="md"
      fullWidth
      dividers
      contentSx={{ color: 'text.primary' }}
    >
      <HFBox sx={{ height: HISTORY_BODY_PX, overflow: 'auto' }}>
        {failed ? (
          <HFBox component="p" sx={{ m: 0 }}>
            History is unavailable.
          </HFBox>
        ) : text == null ? (
          <HFSkeleton variant="rounded" height="100%" />
        ) : (
          <HistoryText text={text} />
        )}
      </HFBox>
    </HFModal>
  );
}

function fillKey(orderId: number, execId: string): string {
  return `${orderId}:${execId}`;
}

/**
 * Signed position of each instrument after each fill on the trade day, across orders, in trade-time order.
 * Starts from 0 because the opening position is not connected.
 */
function positionsAfterFills(orders: readonly OmsOrder[], tradeDay: string): ReadonlyMap<string, number> {
  const fills = orders
    .flatMap((order) => {
      const symbol = dash(order.bbTicker ?? order.instrumentDescription);
      return (order.fills ?? []).filter((fill) => utcDay(fill.tradeTime) === tradeDay).map((fill) => ({ symbol, fill }));
    })
    .sort((left, right) => Date.parse(left.fill.tradeTime) - Date.parse(right.fill.tradeTime));
  const running = new Map<string, number>();
  const after = new Map<string, number>();
  for (const { symbol, fill } of fills) {
    const next = (running.get(symbol) ?? 0) + (fill.side === 'SELL' ? -1 : 1) * Math.abs(fill.qty);
    running.set(symbol, next);
    after.set(fillKey(fill.orderId, fill.execId), next);
  }
  return after;
}

function FillTapeModal({
  order,
  positionAfter,
  onClose,
}: {
  order: OmsOrder | null;
  positionAfter: ReadonlyMap<string, number>;
  onClose: () => void;
}) {
  const open = order != null;
  const fills = useMemo(() => [...(order?.fills ?? [])].sort((left, right) => Date.parse(left.tradeTime) - Date.parse(right.tradeTime)), [order]);
  const allocations = order?.allocations ?? [];
  const tapeRows = useMemo<FillTapeRow[]>(
    () =>
      fills.map((fill) => ({
        id: `fill-${fill.execId}`,
        time: formatUtcDateTime(fill.tradeTime),
        qty: lots(fill.qty),
        positionAfter: signedLots(positionAfter.get(fillKey(fill.orderId, fill.execId))),
        price: formatPrice(fill.price),
        execId: fill.execId,
      })),
    [fills, positionAfter],
  );
  const hostIndex = (() => {
    if (!order || allocations.length === 0) return 0;
    let host = 0;
    let most = -1;
    allocations.forEach((allocation, index) => {
      const qty = allocation.allocatedQty ?? 0;
      if (qty > most) {
        most = qty;
        host = index;
      }
    });
    return host;
  })();
  const hostAccount = allocations[hostIndex]?.clearingAccount?.trim() || 'Unallocated';
  const singleUnallocated = allocations.length <= 1 && hostAccount === 'Unallocated';
  const filled = Math.abs(order?.filledQty ?? 0);
  const target = Math.abs(order?.qty ?? 0);
  const avg = order ? formatPrice(averagePrice(order)) : DASH;
  const fillSpan = fillRangeLabel(fills);
  const symbol = dash(order?.bbTicker ?? order?.instrumentDescription);
  const side = order?.side ?? DASH;
  const status = order ? omsOrderStatusLabel(order.status) : DASH;
  const title = (
    <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <HFBox component="span" sx={{ fontWeight: 800, color: side === 'BUY' ? 'success.main' : side === 'SELL' ? 'error.main' : 'text.primary' }}>
        {side}
      </HFBox>
      <span>
        {lots(target)} {symbol}
      </span>
      <HFBox component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        · #{order?.orderId}
      </HFBox>
      <HFChip tone={statusTone(status)} label={status} />
    </HFBox>
  );

  return (
    <HFModal open={open} onClose={onClose} title={title} maxWidth="md" fullWidth dividers>
      {!order ? null : (
        <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <HFBox sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
            <HFKpiTile label="Filled" value={`${lots(filled)} / ${lots(target)}`} />
            <HFKpiTile label="Avg price" value={avg} />
            <HFKpiTile label="Fills" value={String(fills.length)} />
          </HFBox>
          {fillSpan ? <HFBox sx={{ mt: -1, fontSize: 13, color: 'text.secondary' }}>{fillSpan}</HFBox> : null}
          {singleUnallocated ? (
            <HFBox sx={{ fontSize: 13, color: 'text.secondary' }}>Not yet allocated to a clearing account.</HFBox>
          ) : (
            <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <HFBox sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>Allocations</HFBox>
              <HFBox
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1.6fr) 100px 80px 100px 100px 72px',
                  gap: 1,
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'text.secondary',
                }}
              >
                <span>Clearing account</span>
                <HFBox sx={{ textAlign: 'right' }}>Instruction</HFBox>
                <HFBox sx={{ textAlign: 'right' }}>Alloc. %</HFBox>
                <HFBox sx={{ textAlign: 'right' }}>Allocated</HFBox>
                <HFBox sx={{ textAlign: 'right' }}>Avg fill</HFBox>
                <span />
              </HFBox>
              {allocations.map((allocation, index) => {
                const account = allocation.clearingAccount?.trim() || 'Unallocated';
                const allocated = allocation.allocatedQty ?? 0;
                return (
                  <HFBox
                    key={`${account}-${index}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1.6fr) 100px 80px 100px 100px 72px',
                      gap: 1,
                      alignItems: 'center',
                      fontSize: 13,
                    }}
                  >
                    <span>{account}</span>
                    <HFBox sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{lots(allocation.instructionQty)}</HFBox>
                    <HFBox sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{percent(allocation.allocPct)}</HFBox>
                    <HFBox sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{lots(allocated)}</HFBox>
                    <HFBox sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {allocated > 0 ? formatPrice(allocation.averageFillPrice) : DASH}
                    </HFBox>
                    <HFBox>{index === hostIndex && fills.length > 0 ? <HFChip tone="neutral" label="Fills" /> : null}</HFBox>
                  </HFBox>
                );
              })}
            </HFBox>
          )}
          <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <HFBox sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>{`Fills · shown under ${hostAccount}`}</HFBox>
            {fills.length === 0 ? (
              <HFBox sx={{ fontSize: 13, color: 'text.secondary' }}>No fills yet.</HFBox>
            ) : (
              <HFBox sx={{ height: Math.min(360, 56 + tapeRows.length * 44) }}>
                <HFDataTable<FillTapeRow>
                  rows={tapeRows}
                  columns={[
                    { field: 'time', headerName: 'Date/Time', width: 248, minWidth: 248, sortable: false },
                    { field: 'qty', headerName: 'Qty', width: 96, minWidth: 96, sortable: false },
                    {
                      field: 'positionAfter',
                      headerName: 'Pos after fill',
                      width: 150,
                      minWidth: 150,
                      sortable: false,
                      renderHeader: () => (
                        <HintHeader
                          label="Pos after fill"
                          hint={`This instrument's position after the fill, counting every order's fills on the trade day from 0. ${TODAY_ONLY}`}
                        />
                      ),
                    },
                    { field: 'price', headerName: 'Price', width: 110, minWidth: 110, sortable: false },
                    { field: 'execId', headerName: 'Exec ID', flex: 1, minWidth: 160, sortable: false },
                  ]}
                  columnSearch={false}
                  columnDrag={false}
                  disableColumnMenu
                  hideFooter
                  fill
                  autoHeight={false}
                  autosizeOnMount={false}
                  rowHeight={44}
                  columnHeaderHeight={44}
                  sx={{
                    '& .MuiDataGrid-cell': { fontSize: 15 },
                    '& .MuiDataGrid-columnHeaderTitle': { fontSize: 13 },
                  }}
                />
              </HFBox>
            )}
          </HFBox>
        </HFBox>
      )}
    </HFModal>
  );
}

/** At the broker: their unfilled lots are Working. The cache has no child-order split. */
const LIVE: ReadonlySet<string> = new Set([
  OMS_ORDER_STATUS_LABELS.Submitted,
  OMS_ORDER_STATUS_LABELS.Pending,
  OMS_ORDER_STATUS_LABELS.PartiallyFilled,
]);

const SIDE_LABELS = { BUY: 'Buy', SELL: 'Sell' } as const;

/** Inactive is what is neither filled nor working: the rest of an ended order, and all of an order still in preview. */
function toOrderRow(order: OmsOrder): OrderRow {
  const label = omsOrderStatusLabel(order.status);
  const qty = Math.abs(order.qty);
  const filled = Math.abs(order.filledQty);
  const working = LIVE.has(label) ? Math.max(qty - filled, 0) : 0;
  const details = executionDetails(order);
  const fills = [...(order.fills ?? [])].sort((left, right) => Date.parse(left.tradeTime) - Date.parse(right.tradeTime));
  const avgPrice = averagePrice(order);
  return {
    id: String(order.orderId),
    kind: 'order',
    ...(details ? { children: [details] } : {}),
    exchange: dash(order.exchange),
    product: dash(order.product),
    year: order.isSpread === true ? 'Spread' : dash(order.contractYear),
    month: dash(order.contractMonthCode),
    symbol: dash(order.bbTicker ?? order.instrumentDescription),
    status: label,
    statusTone: statusTone(label),
    side: order.side ? SIDE_LABELS[order.side] : DASH,
    target: qty,
    orderId: String(order.orderId),
    filled,
    working,
    inactive: Math.max(qty - filled - working, 0),
    first: fills.length > 0 ? formatPrice(fills[0]!.price, AVG_DECIMALS) : DASH,
    avg: formatPrice(avgPrice, AVG_DECIMALS),
    last: fills.length > 0 ? formatPrice(fills[fills.length - 1]!.price, AVG_DECIMALS) : DASH,
    avgPrice,
  };
}

/** These orders stopped working. What they did not fill is shown as unmet. */
const ENDED_UNFILLED: ReadonlySet<string> = new Set([
  OMS_ORDER_STATUS_LABELS.Rejected,
  OMS_ORDER_STATUS_LABELS.Void,
  OMS_ORDER_STATUS_LABELS.Cancelled,
  OMS_ORDER_STATUS_LABELS.Expired,
]);

const SUMMARY_STATUS_ORDER: readonly string[] = [
  OMS_ORDER_STATUS_LABELS.Unsubmitted,
  OMS_ORDER_STATUS_LABELS.Submitted,
  OMS_ORDER_STATUS_LABELS.Pending,
  OMS_ORDER_STATUS_LABELS.PartiallyFilled,
  OMS_ORDER_STATUS_LABELS.Filled,
  OMS_ORDER_STATUS_LABELS.Rejected,
  OMS_ORDER_STATUS_LABELS.Cancelled,
  OMS_ORDER_STATUS_LABELS.Expired,
  OMS_ORDER_STATUS_LABELS.Void,
];

/** e.g. `9 Complete · 3 Rejected (30 lots)`. */
function statusCounts(rows: readonly OrderRow[]): string {
  const counts = new Map<string, { orders: number; unmet: number }>();
  for (const row of rows) {
    const entry = counts.get(row.status) ?? { orders: 0, unmet: 0 };
    entry.orders += 1;
    if (ENDED_UNFILLED.has(row.status)) entry.unmet += Math.max(row.target - row.filled, 0);
    counts.set(row.status, entry);
  }
  const labels = [...SUMMARY_STATUS_ORDER, ...[...counts.keys()].filter((label) => !SUMMARY_STATUS_ORDER.includes(label))];
  return labels
    .flatMap((label) => {
      const entry = counts.get(label);
      if (!entry) return [];
      const unmet = entry.unmet === 1 ? ' (1 lot)' : entry.unmet > 0 ? ` (${lots(entry.unmet)} lots)` : '';
      return [`${entry.orders} ${label}${unmet}`];
    })
    .join(' · ');
}

function workingLots(rows: readonly OrderRow[], side: string): number {
  return rows.reduce((total, row) => (row.side === side ? total + row.working : total), 0);
}

/**
 * One row per instrument with the shown orders under it, by exchange, product and symbol.
 * Position and working lots count every order of the instrument, so a status filter does not change them.
 */
function instrumentRows(shown: readonly OrderRow[], all: readonly OrderRow[], positions: ReadonlyMap<string, number> | null): InstrumentRow[] {
  const buckets = new Map<string, OrderRow[]>();
  for (const row of shown) {
    const bucket = buckets.get(row.symbol);
    if (bucket) bucket.push(row);
    else buckets.set(row.symbol, [row]);
  }
  const compare = (left: string, right: string) => left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
  return [...buckets.entries()]
    .map(([symbol, orders]): InstrumentRow => {
      const first = orders[0]!;
      const instrumentOrders = all.filter((row) => row.symbol === symbol);
      const workingBuys = workingLots(instrumentOrders, SIDE_LABELS.BUY);
      const workingSells = workingLots(instrumentOrders, SIDE_LABELS.SELL);
      const position = positions ? (positions.get(symbol) ?? 0) : null;
      return {
        id: `instrument:${symbol}`,
        kind: 'instrument',
        children: orders,
        exchange: first.exchange,
        product: first.product,
        year: first.year,
        month: first.month,
        symbol,
        status: statusCounts(orders),
        position,
        workingBuys,
        workingSells,
        projected: position == null ? null : position + workingBuys - workingSells,
      };
    })
    .sort((left, right) => compare(left.exchange, right.exchange) || compare(left.product, right.product) || compare(left.symbol, right.symbol));
}

function compareOrders(left: InstrumentRow | OrderRow, right: InstrumentRow | OrderRow, sort: OmsLayoutSort | null): number {
  if (!sort) return 0;
  const direction = sort.direction === 'desc' ? -1 : 1;
  const a = (left as Record<string, unknown>)[sort.id];
  const b = (right as Record<string, unknown>)[sort.id];
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * direction;
  if (typeof a !== 'string' || typeof b !== 'string') return 0;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }) * direction;
}

function sortedOrders(orders: OrderRow[], sort: OmsLayoutSort | null): OrderRow[] {
  return sort ? [...orders].sort((left, right) => compareOrders(left, right, sort)) : orders;
}

/** The cell as the grid shows it, except Filled, which is the lot count and not the bar. */
function csvValue(row: InstrumentRow | OrderRow, id: string): string {
  if (row.kind === 'instrument') {
    if (id === 'position' || id === 'projected') return signedLots(row[id]);
    if (id === 'workingBuys' || id === 'workingSells') return lots(row[id]);
  }
  const value = (row as Record<string, unknown>)[id];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function columnLabel(id: string): string {
  return BLOTTER_COLUMNS.find((column) => column.id === id)?.label ?? id;
}

function defaultColumns(): OmsLayoutColumn[] {
  return BLOTTER_COLUMNS.map((column) => ({ id: column.id, width: column.width, visible: true }));
}

function sameColumns(left: OmsLayoutColumn[], right: OmsLayoutColumn[]): boolean {
  return (
    left.length === right.length &&
    left.every((column, index) => {
      const other = right[index];
      return other != null && column.id === other.id && column.visible === other.visible && column.width === other.width;
    })
  );
}

function applyFieldOrder(columns: OmsLayoutColumn[], fields: string[]): OmsLayoutColumn[] {
  const byId = new Map(columns.map((column) => [column.id, column]));
  const next = fields.map((id) => byId.get(id)).filter((column): column is OmsLayoutColumn => column != null);
  const seen = new Set(next.map((column) => column.id));
  for (const column of columns) {
    if (!seen.has(column.id)) next.push(column);
  }
  return next;
}

function sameSort(left: OmsLayoutSort | null, right: OmsLayoutSort | null): boolean {
  return left?.id === right?.id && left?.direction === right?.direction;
}

/** Saved ids win. A column the save does not mention stays at the end, visible, so a later column is not lost. */
function applySaved(saved: OmsLayoutColumn[]): OmsLayoutColumn[] {
  const known = new Map(defaultColumns().map((column) => [column.id, column]));
  const used = new Set<string>();
  const next: OmsLayoutColumn[] = [];
  for (const column of saved) {
    const base = known.get(column.id);
    if (!base || used.has(column.id)) continue;
    used.add(column.id);
    next.push({ id: column.id, width: column.width ?? base.width, visible: column.visible });
  }
  for (const column of defaultColumns()) {
    if (!used.has(column.id)) next.push(column);
  }
  return next;
}

function knownSort(sort: OmsLayoutSort | undefined, columns: OmsLayoutColumn[]): OmsLayoutSort | null {
  if (!sort || !columns.some((column) => column.id === sort.id)) return null;
  return { id: sort.id, direction: sort.direction };
}

function EmptyOrders({ message }: { message: string }) {
  return <HFBox sx={{ px: 2, py: 3, color: 'text.secondary', fontSize: 14 }}>{message}</HFBox>;
}

/** Order Monitor blotter. Visibility is edited in the modal. Order, width and sort come from the grid and are saved. */
export function OrderBlotter({
  date,
  orders,
  ordersFailed,
  positions,
  compact = false,
  initialFilters,
  onFiltersChange,
  initialColumns,
  initialSort,
  onColumnsChange,
}: {
  date?: string;
  /** Null until the first read for this day lands. */
  orders: OmsOrder[] | null;
  ordersFailed: boolean;
  positions: ReadonlyMap<string, number> | null;
  /** Inside the Custom layout: a one-line header, with the toolbar behind a Filters toggle. */
  compact?: boolean;
  /** Read once, when the blotter mounts. */
  initialFilters?: OmsOrderBlotterFilters;
  onFiltersChange?: (filters: OmsOrderBlotterFilters) => void;
  /** Compact only, read once. The Custom layout keeps the columns and never touches the personal layout. */
  initialColumns?: OmsLayoutColumn[];
  initialSort?: OmsLayoutSort;
  onColumnsChange?: (columns: OmsLayoutColumn[], sort?: OmsLayoutSort) => void;
}) {
  const { request } = useHttp();
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter | null>(initialFilters?.status ?? null);
  const [columns, setColumns] = useState<OmsLayoutColumn[]>(() => (compact && initialColumns ? applySaved(initialColumns) : defaultColumns()));
  const [sort, setSort] = useState<OmsLayoutSort | null>(() =>
    compact && initialColumns ? knownSort(initialSort, applySaved(initialColumns)) : null,
  );
  const [ready, setReady] = useState(compact);
  const [editing, setEditing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [columnQueries, setColumnQueries] = useState<Record<string, string>>(() =>
    Object.fromEntries((initialFilters?.columnQueries ?? []).map(({ id, query }) => [id, query])),
  );
  const [chromeOpen, setChromeOpen] = useState(false);
  const [draft, setDraft] = useState<OmsLayoutColumn[]>(defaultColumns);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<GridRowId>>(() => new Set());
  const [fillsOrderId, setFillsOrderId] = useState<string | null>(null);
  const [historyOrderId, setHistoryOrderId] = useState<string | null>(null);
  const [shownDate, setShownDate] = useState(date);
  if (shownDate !== date) {
    setShownDate(date);
    setExpandedIds(new Set());
    setFillsOrderId(null);
  }
  const columnsRef = useRef(columns);
  const sortRef = useRef(sort);
  columnsRef.current = columns;
  sortRef.current = sort;
  const requestRef = useRef(request);
  requestRef.current = request;
  const onColumnsChangeRef = useRef(onColumnsChange);
  onColumnsChangeRef.current = onColumnsChange;

  useEffect(() => {
    if (compact) return;
    let cancelled = false;
    const layout = loadMonitorLayout(request);
    void layout.result.then((stored) => {
      if (cancelled) return;
      const next = stored ? applySaved(stored.columns) : defaultColumns();
      syncMonitorLayout(stored);
      setColumns(next);
      setSort(stored ? knownSort(stored.sort, next) : null);
      setReady(true);
    });
    return () => {
      cancelled = true;
      layout.cancel();
    };
  }, [compact, request]);

  const orderRows = useMemo(() => (orders ?? []).map(toOrderRow), [orders]);
  const shownOrders = useMemo(
    () => (statusFilter ? orderRows.filter((row) => statusFilter.statuses.includes(row.status)) : orderRows),
    [orderRows, statusFilter],
  );
  const ordersById = useMemo(() => new Map((orders ?? []).map((order) => [String(order.orderId), order])), [orders]);
  const fillsOrder = fillsOrderId ? (ordersById.get(fillsOrderId) ?? null) : null;
  const tradeDay = date ?? new Date().toISOString().slice(0, 10);
  const positionAfter = useMemo(() => positionsAfterFills(orders ?? [], tradeDay), [orders, tradeDay]);
  const instruments = useMemo(
    () => instrumentRows(filterRowsByFields(shownOrders, columnQueries), orderRows, positions),
    [columnQueries, orderRows, positions, shownOrders],
  );
  const settled = ready && orders !== null;
  const parentCount = orders?.length ?? 0;
  const serverSearch = useMemo(
    () => ({
      fetch: ({ columns: queries }: { columns: Record<string, string> }) =>
        Promise.resolve(instrumentRows(filterRowsByFields(shownOrders, queries), orderRows, positions)),
      debounceMs: 0,
    }),
    [orderRows, positions, shownOrders],
  );
  const rowTree = useMemo<HFDataTableRowTree<BlotterRow>>(
    () => ({
      getChildren: (row) => {
        if (row.kind === 'order') return row.children;
        if (row.kind === 'execution') return undefined;
        return sortedOrders(row.children, sort);
      },
      panelFromDepth: 2,
      renderChildRow: (row) => (row.kind === 'execution' ? renderExecutionPanel(row, setFillsOrderId) : null),
      getToggleLabel: (row, expanded) => {
        const verb = expanded ? 'Collapse' : 'Expand';
        if (row.kind === 'order') return `${verb} order ${row.orderId}`;
        if (row.kind === 'instrument') return `${verb} instrument ${row.symbol}`;
        return `${verb} row ${row.id}`;
      },
      expandedIds,
      onExpandedIdsChange: setExpandedIds,
    }),
    [expandedIds, sort],
  );

  const gridColumns = useMemo(() => {
    const data = columns.map((column) => {
      const base = {
        field: column.id,
        headerName: columnLabel(column.id),
        width: column.width,
        minWidth: OMS_LAYOUT_COLUMN_MIN_WIDTH,
        maxWidth: OMS_LAYOUT_COLUMN_MAX_WIDTH,
      };
      const hint = BLOTTER_COLUMN_DETAILS[column.id as OmsBlotterColumnId]?.hint;
      if (hint) {
        const field = column.id as 'position' | 'workingBuys' | 'workingSells' | 'projected';
        const signed = field === 'position' || field === 'projected';
        return {
          ...base,
          sortable: false,
          filterable: false,
          renderHeader: () => <HintHeader label={base.headerName} hint={hint} />,
          renderCell: ({ row }: { row: BlotterRow }) => {
            if (row.kind !== 'instrument') return null;
            return signed ? signedLots(row[field]) : lots(row[field] ?? 0);
          },
        };
      }
      if (column.id === 'status') {
        return {
          ...base,
          renderCell: ({ row }: { row: BlotterRow }) => {
            if (row.kind === 'execution') return null;
            if (row.kind === 'instrument') {
              return (
                <HFBox component="span" title={row.status} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.status}
                </HFBox>
              );
            }
            return (
              <HFChip
                tone={row.statusTone}
                label={row.status}
                title={row.status === OMS_ORDER_STATUS_LABELS.Pending ? OMS_RECEIVED_HINT : undefined}
              />
            );
          },
        };
      }
      if (column.id === 'filled') {
        return {
          ...base,
          renderCell: ({ row }: { row: BlotterRow }) => (row.kind === 'order' ? <FillBar row={row} /> : null),
          valueGetter: (_value: unknown, row: BlotterRow) => (row.kind === 'order' ? row.filled : ''),
        };
      }
      return base;
    });
    return [
      ...data,
      actionsColumn<BlotterRow>({
        width: 56,
        actions: [
          {
            icon: <HistoryIcon />,
            label: 'History',
            inMenu: true,
            hidden: (row) => row.kind !== 'order',
            onClick: (row) => {
              if (row.kind === 'order') setHistoryOrderId(row.orderId);
            },
          },
          {
            icon: <PauseIcon />,
            label: 'Pause',
            inMenu: true,
            disabled: () => true,
            hidden: (row) => row.kind !== 'order',
            onClick: () => undefined,
          },
          {
            icon: <EditIcon />,
            label: 'Modify',
            inMenu: true,
            disabled: () => true,
            hidden: (row) => row.kind !== 'order',
            onClick: () => undefined,
          },
          {
            icon: <PlayArrowIcon />,
            label: 'Resume',
            inMenu: true,
            disabled: () => true,
            hidden: (row) => row.kind !== 'order',
            onClick: () => undefined,
          },
          {
            icon: <CancelIcon />,
            label: 'Cancel',
            inMenu: true,
            disabled: () => true,
            hidden: (row) => row.kind !== 'order',
            onClick: () => undefined,
          },
        ],
      }),
    ];
  }, [columns]);
  const visibility = useMemo(() => Object.fromEntries(columns.map((column) => [column.id, column.visible])), [columns]);
  const visibleDraft = draft.filter((column) => column.visible);
  const draftChanged = !sameColumns(draft, columns);
  const filtersActive = Object.values(columnQueries).some((query) => query.trim() !== '');
  const filters = useMemo<OmsOrderBlotterFilters>(() => {
    const queries = Object.entries(columnQueries)
      .filter(([id, query]) => query.trim() && (OMS_BLOTTER_COLUMN_IDS as readonly string[]).includes(id))
      .map(([id, query]) => ({ id: id as OmsBlotterColumnId, query: query.trim().slice(0, 64) }));
    return {
      ...(statusFilter ? { status: { name: statusFilter.name, statuses: [...statusFilter.statuses] } } : {}),
      ...(queries.length ? { columnQueries: queries } : {}),
    };
  }, [columnQueries, statusFilter]);
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  useEffect(() => onFiltersChangeRef.current?.(filters), [filters]);
  const summary =
    [statusFilter?.name, ...(filters.columnQueries ?? []).map((item) => `${columnLabel(item.id)}: ${item.query}`)].filter(Boolean).join(', ') ||
    'All orders';
  const executionHeights = useMemo(() => {
    const heights = new Map<string, number>();
    for (const order of orderRows) {
      const execution = order.children?.[0];
      if (execution) heights.set(execution.id, executionPanelHeight(execution.accounts.length));
    }
    return heights;
  }, [orderRows]);

  const openEditor = () => {
    setDraft(columns.map((column) => ({ ...column })));
    setEditing(true);
  };

  const keptSort = (nextColumns: OmsLayoutColumn[], nextSort: OmsLayoutSort | null): OmsLayoutSort | null =>
    nextSort && nextColumns.some((column) => column.id === nextSort.id && column.visible) ? nextSort : null;

  const writeLayout = (nextColumns: OmsLayoutColumn[], nextSort: OmsLayoutSort | null, close: boolean) => {
    const sortToStore = keptSort(nextColumns, nextSort);
    if (compact) {
      setColumns(nextColumns);
      setSort(sortToStore);
      if (close) setEditing(false);
      onColumnsChangeRef.current?.(nextColumns, sortToStore ?? undefined);
      return Promise.resolve(true);
    }
    if (close) setApplying(true);
    else setApplying(false);
    return queueMonitorLayoutSave(requestRef.current, { columns: nextColumns, sort: sortToStore }).then((savedOk) => {
      if (savedOk && close) {
        setColumns(nextColumns);
        setSort(sortToStore);
        setEditing(false);
      }
      if (savedOk) track(GA.savedOmsColumnLayout, { layout: TAB });
      setApplying(false);
      return savedOk;
    });
  };

  /** The filtered instruments in screen order, each followed by its orders. Execution panels are not written. */
  const exportCsv = () => {
    const visible = columns.filter((column) => column.visible);
    const sortedInstruments = sort ? [...instruments].sort((left, right) => compareOrders(left, right, sort)) : instruments;
    const lines = sortedInstruments.flatMap((instrument) =>
      [instrument, ...sortedOrders(instrument.children, sort)].map((row) => visible.map((column) => csvValue(row, column.id))),
    );
    downloadCsv(
      csvFileName('oms-order-blotter', date),
      toCsv(
        visible.map((column) => columnLabel(column.id)),
        lines,
      ),
    );
    track(GA.exportedOmsGridCsv, { layout: 'orders', format: 'csv' });
  };

  const onColumnsReorder = (fields: string[]) => {
    if (!ready) return;
    const current = columnsRef.current;
    const next = applyFieldOrder(current, fields);
    if (sameColumns(next, current)) return;
    setColumns(next);
    void writeLayout(next, sortRef.current, false).then((savedOk) => {
      if (!savedOk) setColumns(current);
    });
  };

  const onSortModelChange = (model: ReadonlyArray<{ field: string; sort?: string | null }>) => {
    if (!ready) return;
    const picked = model.find((item) => item.sort === 'asc' || item.sort === 'desc');
    const nextSort: OmsLayoutSort | null = picked ? { id: picked.field, direction: picked.sort === 'desc' ? 'desc' : 'asc' } : null;
    if (sameSort(nextSort, sort)) return;
    const previous = sort;
    setSort(nextSort);
    void writeLayout(columns, nextSort, false).then((savedOk) => {
      if (!savedOk) setSort(previous);
    });
  };

  const onColumnWidthChange = ({ colDef, width }: { colDef: { field: string }; width: number }) => {
    if (!ready) return;
    const current = columnsRef.current;
    const next = current.map((column) => (column.id === colDef.field ? { ...column, width } : column));
    if (sameColumns(next, current)) return;
    setColumns(next);
    void writeLayout(next, sortRef.current, false).then((savedOk) => {
      if (!savedOk) setColumns(current);
    });
  };

  return (
    <HFCard component="section" aria-label="Order blotter" aria-busy={!settled} sx={monitorPaneSx}>
      {compact ? <PanelHeader title="Order blotter" summary={summary} open={chromeOpen} onToggle={() => setChromeOpen((open) => !open)} /> : null}
      {!compact || chromeOpen ? (
        <HFBox sx={CHROME_SX}>
          <HFBox sx={CHROME_ROW_SX}>
            {compact ? null : (
              <HFBox sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                <HFBox component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>
                  Order blotter
                </HFBox>
                {settled && !ordersFailed ? (
                  <HFBox component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {statusFilter ? `${shownOrders.length} of ` : ''}
                    {parentCount === 1 ? '1 parent order' : `${parentCount} parent orders`}
                  </HFBox>
                ) : null}
              </HFBox>
            )}
            <HFBox sx={{ ...CLUSTER_SX, ml: 'auto' }}>
              <HFButton
                size="small"
                sx={COMPACT_BUTTON_SX}
                onClick={() => {
                  setColumnQueries({});
                  setStatusFilter(null);
                }}
                disabled={!settled || !(filtersActive || statusFilter)}
              >
                Reset all filters
              </HFButton>
              <HFButton size="small" variant="outlined" sx={COMPACT_BUTTON_SX} onClick={openEditor} disabled={!settled}>
                Add/remove columns
              </HFButton>
              <HFButton size="small" variant="outlined" sx={COMPACT_BUTTON_SX} onClick={exportCsv} disabled={!settled}>
                Export
              </HFButton>
            </HFBox>
          </HFBox>
          <OrderStatusStrip orders={orders} failed={ordersFailed} selected={statusFilter} onSelect={setStatusFilter} />
        </HFBox>
      ) : null}
      <HFBox sx={{ flex: 1, minHeight: 0 }}>
        {settled ? (
          <HFDataTable<BlotterRow>
            key={`${columns
              .map((column) => `${column.id}:${column.visible ? 1 : 0}`)
              .sort()
              .join('|')}|${orderRows.map((row) => row.id).join(',')}`}
            rows={instruments}
            columns={gridColumns}
            columnVisibilityModel={visibility}
            sortModel={sort ? [{ field: sort.id, sort: sort.direction }] : []}
            onSortModelChange={onSortModelChange}
            onColumnsReorder={onColumnsReorder}
            onColumnWidthChange={onColumnWidthChange}
            columnQueries={columnQueries}
            onColumnQueriesChange={setColumnQueries}
            rowTree={rowTree}
            serverSearch={serverSearch}
            getRowHeight={({ id }) => executionHeights.get(String(id)) ?? BLOTTER_ROW_PX}
            disableColumnMenu
            hideFooter
            fill
            autoHeight={false}
            autosizeOnMount={false}
            disableColumnVirtualization
            sx={{
              '& .MuiDataGrid-columnHeader[data-field="actions"]': {
                position: 'sticky',
                right: 0,
                zIndex: 31,
                bgcolor: 'background.paper',
                boxShadow: (theme) => `inset 1px 0 0 ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-cell[data-field="actions"]': {
                position: 'sticky',
                right: 0,
                zIndex: 2,
                bgcolor: 'background.paper',
                backgroundImage: 'linear-gradient(var(--hf-row-bg, transparent), var(--hf-row-bg, transparent))',
                boxShadow: (theme) => `inset 1px 0 0 ${theme.palette.divider}`,
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <EmptyOrders
                  message={
                    ordersFailed ? 'Orders unavailable.' : orderRows.length > 0 ? 'No orders match this filter.' : 'No orders for this trade day.'
                  }
                />
              ),
            }}
          />
        ) : (
          <TableSkeleton testId="layout-skeleton" />
        )}
      </HFBox>
      <HFModal
        open={editing}
        onClose={() => setEditing(false)}
        title="Add/remove columns"
        maxWidth="md"
        fullWidth
        dividers
        actions={
          <>
            <HFButton variant="outlined" onClick={() => setEditing(false)}>
              Cancel
            </HFButton>
            <HFButton loading={applying} onClick={() => void writeLayout(draft, sort, true)} disabled={!draftChanged || visibleDraft.length === 0}>
              Apply
            </HFButton>
          </>
        }
      >
        <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <HFBox sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' }}>Columns</HFBox>
          <HFBox sx={{ fontSize: 13, color: 'text.secondary' }}>{`${visibleDraft.length} shown.`}</HFBox>
          <ColumnVisibilityList columns={draft} label={columnLabel} onChange={setDraft} />
        </HFBox>
      </HFModal>
      <FillTapeModal order={fillsOrder} positionAfter={positionAfter} onClose={() => setFillsOrderId(null)} />
      <OrderHistoryModal orderId={historyOrderId} onClose={() => setHistoryOrderId(null)} />
    </HFCard>
  );
}
